<template>
  <ModalTabs :tabs="tabs" @onSetTab="setActiveTab" />
  <div :class="`lf-modal__content-tab lf-modal__content-tab--${stringToSlug(selectedTab)}`">
    <div class="lf-modal-wrapper lf-modal-wrapper--with-sidebar" :class="wrapperClasses">
      <div class="lf-modal-wrapper__sidebar">
        <div class="lf-modal-wrapper__sidebar-inner">
          <transition name="fade">
            <ModalSidebarCategories
              v-if="categories && categories?.length > 0"
              :active-category="selectedCategory"
              :categories="categories"
              @setActiveCategory="onUpdateCategory"
            />
          </transition>
        </div>
      </div>
      <div class="lf-modal-wrapper__content">
        <div class="lf-modal-wrapper__content-inner" :class="{ 'pointer-events-none': isAddingItem }">
          <transition name="fade">
            <ModalBlocks
              v-if="blocks && blocks?.length > 0"
              :type="type"
              :determine-show-icon="determineShowIcon ? determineShowIcon : ''"
              :categories="categories"
              :blocks="blocks"
              @addItem="onAddItem"
              @navigateToFlowPages="handleRefetchBlocks"
              @navigateToSections="handleRefetchBlocks"
            />
          </transition>

          <div v-if="blocks && blocks?.length === 0 && !isLoading" class="lf-modal-wrapper__content-empty">
            <i class="lf-modal-wrapper__content-empty-icon icon-empty-circle"></i>
            <template v-if="type === BlockTypes.SECTIONS">No sections added</template>
            <template v-if="type === BlockTypes.BLOCKS">No rows added</template>
            <template v-if="type === BlockTypes.PAGES_FLOW">No pages added</template>
            <template v-if="type === BlockTypes.PAGES_PAGE">No pages added</template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, PropType, ref } from 'vue';
import ModalTabs from '@/components/components/modals/ModalTabs.vue';
import { ConfigData } from '@/Config';
import useAxios from '@/hooks/useAxios';
import ModalSidebarCategories from '@/components/components/modals/ModalSidebarCategories.vue';
import ModalBlocks from '@/components/components/editing/Modals/Content/ModalBlocks.vue';
import { stringToSlug } from '@/utilities/Utilities';
import { BlockTypes, SectionModelType } from '@/typings/types/types';
import { CampaignModel } from '@/models/CampaignModel';
import { APIBlock, APICategory, APIResponse, TabItem } from '@/components/components/editing/Modals/interfaces';
import { useStore } from '@/store';
import { useRoute, useRouter } from 'vue-router';
import useFlow from '@/hooks/useFlow';
import { ContentColumnData } from '@/typings/interfaces/data/grid/column';
import { SectionBaseModel } from '@/components/layout/section/SectionBaseModel';
import { FlowpageModel } from '@/components/layout/FlowpageModel';
import { PopoverModel } from '@/models/PopoverModel';

const tabItemsSections: TabItem[] = [
  {
    id: 1,
    label: 'Grid',
    show: 'grid',
    placement: 'under',
    relative: 'no-sections'
  },
  {
    id: 2,
    label: 'Reuse section',
    show: 'copy',
    placement: 'under',
    relative: 'no-sections'
  },
  {
    id: 3,
    label: 'Templates',
    show: 'templates',
    placement: 'under',
    relative: 'no-sections'
  },
  {
    id: 4,
    label: 'Global',
    show: 'global',
    placement: 'under',
    relative: 'no-sections'
  }
];
const tabItemsRows: TabItem[] = [
  {
    id: 1,
    label: 'Grid',
    show: 'grid',
    placement: 'under',
    relative: 'no-sections'
  }
];
const tabItemsFlow: TabItem[] = [
  {
    id: 1,
    label: 'Grid',
    show: 'grid'
  },
  {
    id: 2,
    label: 'Reuse flow page',
    show: 'copy'
  },
  {
    id: 3,
    label: 'Templates',
    show: 'templates'
  },
  {
    id: 4,
    label: 'Global',
    show: 'global'
  }
];
const tabItemsPopovers: TabItem[] = [
  {
    id: 1,
    label: 'Grid',
    show: 'grid'
  },
  {
    id: 2,
    label: 'Reuse popover',
    show: 'copy'
  },
  {
    id: 3,
    label: 'Templates',
    show: 'templates'
  },
  {
    id: 4,
    label: 'Global',
    show: 'global'
  }
];

interface DefaultParams {
  category: string;
  subcategory?: string;
  show: string;
  type: BlockTypes;
}

export default defineComponent({
  name: 'ModalContentAddContent',
  components: {
    ModalBlocks,
    ModalSidebarCategories,
    ModalTabs
  },
  inheritAttrs: false,
  props: {
    type: {
      type: String as PropType<BlockTypes>,
      required: true
    },
    contextModel: {
      type: Object as PropType<SectionModelType>
    }
  },
  emits: ['close-modal'],
  setup(props, context) {
    const { makeSpecificFlowPageActive } = useFlow();
    const router = useRouter();
    const route = useRoute();
    const selectedTab = ref('');
    const selectedCategory = ref<string | number>('all');
    const store = useStore();
    const campaignModel = store.getters['campaign/model'] as CampaignModel;
    const tabs = ref<TabItem[]>([]);
    const isLoading = ref(true);
    const isSaving = ref(false);
    const isAddingItem = ref(false);
    const categories = ref<APICategory[]>([]);
    const blocks = ref<APIBlock[]>([]);
    const defaultParameters: DefaultParams = {
      category: 'all',
      subcategory: '',
      show: 'grid',
      type: props.type
    };

    if (props.type.toLowerCase() === BlockTypes.SECTIONS) {
      if (tabItemsSections[0] && tabItemsSections[0]?.label) {
        selectedTab.value = tabItemsSections[0].label.toLocaleLowerCase();
      }
      tabs.value = tabItemsSections;
    }
    if (props.type.toLowerCase() === BlockTypes.BLOCKS) {
      if (tabItemsRows[0] && tabItemsRows[0]?.label) {
        selectedTab.value = tabItemsRows[0].label.toLocaleLowerCase();
      }
      tabs.value = tabItemsRows;
    }
    if (props.type.toLowerCase() === BlockTypes.PAGES_FLOW) {
      if (tabItemsFlow[0] && tabItemsFlow[0]?.label) {
        selectedTab.value = tabItemsFlow[0].label.toLocaleLowerCase();
      }
      tabs.value = tabItemsFlow;
    }
    if (props.type.toLowerCase() === BlockTypes.PAGES_PAGE) {
      if (tabItemsPopovers[0] && tabItemsPopovers[0]?.label) {
        selectedTab.value = tabItemsPopovers[0].label.toLocaleLowerCase();
      }
      tabs.value = tabItemsPopovers;
    }

    const handleRefetchBlocks = (categoryId: string) => {
      isLoading.value = true;
      defaultParameters.subcategory = categoryId;
      onFetchEditBlocks();
    };

    const onFetchEditBlocks = async () => {
      const categoriesUrl = `${ConfigData.adminUiEndpoint}/api/v1/campaign/edit/fetchEditBlocks?campaign_id=${
        campaignModel.state.id
      }&category=${defaultParameters.category}&subcategory=${defaultParameters.subcategory}&show=${
        defaultParameters.show
      }&type=${defaultParameters.type.toLowerCase()}`;
      const { fetchData } = useAxios<APIResponse>(categoriesUrl);
      const response = await fetchData();

      isLoading.value = false;
      categories.value = response.categories;
      blocks.value = response.blocks;
    };

    const determineShowIcon = computed(() => {
      if (tabs.value && tabs.value.length > 0) {
        const item = tabs.value.find((tab) => tab.label?.toLocaleLowerCase() === selectedTab.value);
        if (item) {
          return item.show;
        }
      }
      return '';
    });

    const setActiveTab = async (value: TabItem) => {
      categories.value = [];
      blocks.value = [];
      isLoading.value = true;
      if (value?.show) {
        defaultParameters.show = value.show;
      }
      selectedCategory.value = 'all';
      await onFetchEditBlocks();
      if (value?.label) {
        selectedTab.value = value.label.toLocaleLowerCase();
      }
    };

    const onUpdateCategory = async (categoryId: string) => {
      defaultParameters.category = categoryId;
      await onFetchEditBlocks();
      selectedCategory.value = categoryId;
    };

    const onAddItem = async (value: ContentColumnData[] | SectionBaseModel) => {
      isAddingItem.value = true;

      const editingState = store.state.editing;
      const activeModel = editingState.activeModel;
      let section: SectionModelType | undefined = undefined;
      if (activeModel instanceof SectionBaseModel) {
        section = activeModel;
      } else if (props.contextModel) {
        section = props.contextModel;
      } else if (activeModel) {
        section = activeModel.getSection();
      }

      if (Array.isArray(value) && section) {
        await section.onAddNewRow(value);
        await section.saveBlock();
        context.emit('close-modal');
        return;
      }

      if (!Array.isArray(value)) {
        const section = await campaignModel.onAddSection(value, props.type);

        if (section instanceof FlowpageModel) {
          await makeSpecificFlowPageActive(section.state.id); //Used to be originalId
        } else if (section instanceof PopoverModel) {
          const pageId = section.state.id.toString();

          if (!route.query.pageId || route.query.pageId !== pageId) {
            await router.push({
              query: {
                ...route.query,
                pageId
              }
            });
          }
        }
      }

      isAddingItem.value = false;
      context.emit('close-modal');
    };

    const wrapperClasses = computed(() => {
      return {
        'lf-modal-wrapper--loading': isLoading.value,
        'lf-modal-wrapper--saving': isSaving.value
      };
    });

    onMounted(() => {
      onFetchEditBlocks();
    });

    return {
      handleRefetchBlocks,
      onUpdateCategory,
      determineShowIcon,
      selectedCategory,
      selectedTab,
      blocks,
      categories,
      tabs,
      isAddingItem,
      isLoading,
      isSaving,
      setActiveTab,
      stringToSlug,
      onAddItem,
      wrapperClasses,
      BlockTypes
    };
  }
});
</script>
